// frontend/src/components/common/GlobalOverlay/AnnouncementBanner.js
import React, { useState, useEffect, useMemo } from 'react';
import { X, Megaphone, AlertCircle, ChevronRight } from 'lucide-react';
import useAnnouncementStore from '../../../store/announcementStore';
import { useOverlayAnimation } from './useOverlayAnimation';

const AnnouncementBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const { isVisible, animateIn, animateOut } = useOverlayAnimation(400);
  
  const {
    announcements,
    isLoading,
    getActiveAnnouncements,
    dismissAnnouncement,
    trackAnnouncementClick,
    shouldShowAnnouncement,
    getVisibleAnnouncements,
  } = useAnnouncementStore();

  // Load announcements on mount
  useEffect(() => {
    getActiveAnnouncements();
  }, []);

  // Get visible announcements
  const visibleAnnouncements = useMemo(() => {
    return getVisibleAnnouncements();
  }, [announcements]);

  // Show/hide announcements
  useEffect(() => {
    if (visibleAnnouncements.length > 0 && currentIndex < visibleAnnouncements.length) {
      const announcement = visibleAnnouncements[currentIndex];
      if (shouldShowAnnouncement(announcement.id)) {
        setVisible(true);
        animateIn();
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    } else {
      setVisible(false);
      animateOut();
    }
  }, [visibleAnnouncements, currentIndex]);

  const handleDismiss = () => {
    const announcement = visibleAnnouncements[currentIndex];
    if (!announcement) return;

    dismissAnnouncement(announcement.id);
    
    if (currentIndex < visibleAnnouncements.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      animateOut(() => {
        setVisible(false);
        setCurrentIndex(0);
      });
    }
  };

  const handleClick = async () => {
    const announcement = visibleAnnouncements[currentIndex];
    if (!announcement) return;

    await trackAnnouncementClick(announcement.id);

    // Handle CTA
    if (announcement.cta) {
      const cta = announcement.cta;
      if (cta.action === 'OPEN_URL' && cta.url) {
        window.open(cta.url, '_blank');
      } else if (cta.action === 'OPEN_DEEPLINK' && cta.deepLink) {
        window.location.href = cta.deepLink;
      } else if (cta.action === 'OPEN_BUSINESS' && announcement.promotion?.business?.slug) {
        window.location.href = `/community/marketplace/business/${announcement.promotion.business.slug}`;
      }
    }
  };

  if (!visible || isLoading || visibleAnnouncements.length === 0) {
    return null;
  }

  const announcement = visibleAnnouncements[currentIndex];
  if (!announcement) return null;

  const isUrgent = announcement.isUrgent;
  const priority = announcement.priority;

  const getStyles = () => {
    if (isUrgent || priority === 'URGENT') {
      return {
        bg: 'bg-gradient-to-r from-red-50 to-orange-50',
        border: 'border-red-300',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        titleColor: 'text-red-900',
        actionColor: 'bg-red-600 hover:bg-red-700'
      };
    } else if (priority === 'HIGH') {
      return {
        bg: 'bg-gradient-to-r from-orange-50 to-amber-50',
        border: 'border-orange-300',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
        titleColor: 'text-orange-900',
        actionColor: 'bg-orange-600 hover:bg-orange-700'
      };
    } else {
      return {
        bg: 'bg-gradient-to-r from-blue-50 to-indigo-50',
        border: 'border-blue-200',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        titleColor: 'text-blue-900',
        actionColor: 'bg-blue-600 hover:bg-blue-700'
      };
    }
  };

  const styles = getStyles();

  return (
    <div className={`relative ${styles.bg} border-l-4 ${styles.border} rounded-lg shadow-lg p-3 mb-2 transition-all duration-300 animate-slide-up`}>
      <div className="flex items-start gap-3">
        <div className={`p-1.5 ${styles.iconBg} rounded-lg flex-shrink-0 mt-0.5`}>
          {isUrgent ? (
            <AlertCircle className={`h-4 w-4 ${styles.iconColor}`} />
          ) : (
            <Megaphone className={`h-4 w-4 ${styles.iconColor}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`text-sm font-semibold ${styles.titleColor}`}>
              {announcement.title}
            </h4>
            {isUrgent && (
              <span className="inline-flex items-center px-2 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">
                URGENT
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 mt-0.5">
            {announcement.content}
          </p>
          
          {announcement.cta?.text && (
            <button
              onClick={handleClick}
              className={`mt-2 inline-flex items-center px-3 py-1 ${styles.actionColor} text-white text-xs font-medium rounded-lg transition-all shadow-md hover:shadow-lg`}
            >
              {announcement.cta.text}
              <ChevronRight className="h-3 w-3 ml-1" />
            </button>
          )}
        </div>
        
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0"
          aria-label="Dismiss announcement"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Dots indicator for multiple announcements */}
      {visibleAnnouncements.length > 1 && (
        <div className="flex justify-center gap-1 mt-2">
          {visibleAnnouncements.map((_, index) => (
            <div
              key={index}
              className={`h-1 w-1 rounded-full transition-all ${
                index === currentIndex ? 'bg-blue-600 w-2' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AnnouncementBanner;